import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '../services/api';

interface User {
  id: number;
  username: string;
  email: string;
  mfaEnabled: boolean;
  name?: string;
  surname?: string;
  clients?: { id: number }[]; // If populated, user is a client user
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  tempToken: string | null;
  isStaffUser: boolean; // True if user is not a client user (has no clients)
  login: (username: string, password: string) => Promise<{ success: boolean; requiresMFA?: boolean; needsMfaSetup?: boolean; error?: string }>;
  verifyMFA: (token: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [tempToken, setTempToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing token on mount
    const savedToken = localStorage.getItem('auth_token');
    if (savedToken) {
      setToken(savedToken);
      apiService.setToken(savedToken);
      fetchUserInfo(savedToken);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserInfo = async (authToken: string) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:1337';
      // Explicitly populate clients relation to detect client vs staff users
      const response = await fetch(`${apiUrl}/api/users/me?populate[clients]=*`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        // Token is invalid, clear it
        localStorage.removeItem('auth_token');
        setToken(null);
      }
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      localStorage.removeItem('auth_token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username: string, password: string) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:1337';
      const response = await fetch(`${apiUrl}/api/auth/local`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          identifier: username,
          password: password
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Check if user has MFA enabled
        if (data.requiresMFA) {
          // Store temporary token for MFA verification
          setTempToken(data.jwt);
          return { success: true, requiresMFA: true };
        } else if (data.needsMfaSetup) {
          // Store temporary token for MFA setup
          setTempToken(data.jwt);
          setUser(data.user);
          return { success: true, needsMfaSetup: true };
        } else {
          // No MFA, complete login
          setToken(data.jwt);
          apiService.setToken(data.jwt);
          setUser(data.user);
          localStorage.setItem('auth_token', data.jwt);
          return { success: true };
        }
      } else {
        return { success: false, error: data.message || 'Login failed' };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const verifyMFA = async (mfaToken: string) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:1337';
      const response = await fetch(`${apiUrl}/api/auth/mfa/login-verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tempToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token: mfaToken })
      });

      const data = await response.json();

      if (response.ok) {
        setToken(data.jwt);
        apiService.setToken(data.jwt);
        setUser(data.user);
        localStorage.setItem('auth_token', data.jwt);
        return { success: true };
      } else {
        return { success: false, error: data.message || 'MFA verification failed' };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setTempToken(null);
    apiService.setToken(null);
    localStorage.removeItem('auth_token');
  };

  // User is staff if they have no clients (client users have clients populated)
  const isStaffUser = !user?.clients || user.clients.length === 0;

  const value: AuthContextType = {
    user,
    token,
    tempToken,
    isStaffUser,
    login,
    verifyMFA,
    logout,
    loading
  };

  return React.createElement(
    AuthContext.Provider,
    { value },
    children
  );
};